import { Disclosure } from "@headlessui/react";
import { useEffect, useMemo, useState } from "react";
import LoadingScreen from "../components/LoadingScreen";
import { sdkWrapperURL } from "../utils/api-url-list";

const LabPackages = () => {
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);

  const [btnLoading, setBtnLoading] = useState(false);
  const [pincode, setPincode] = useState("");
  const [pincodeAvailability, setPincodeAvailability] = useState("none");
  const [selectedPackageID, setSelectedPackageID] = useState("");
  const selectedPackage = useMemo(() => {
    setPincode("");
    setPincodeAvailability("none");
    return packages.find((p) => p.product_id === selectedPackageID);
  }, [packages, selectedPackageID]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = Object.fromEntries(new URLSearchParams(document.location.search).entries());
        if (params?.token?.length > 0) {
          const resp = await fetch(sdkWrapperURL("/users/list/products"), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: params.token },
            body: JSON.stringify({ product_type_id: 1, table_config_rows_per_page: 100, table_config_page_no: 1 }),
          });
          const resp_json = await resp.json();
          if (resp_json.statusCode?.toString().startsWith("2")) {
            setPackages(resp_json.response);
          } else throw new Error(resp_json.message);
        }
      } catch {
        setPackages([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const checkPincode = async () => {
    setBtnLoading(true);
    try {
      const params = Object.fromEntries(new URLSearchParams(document.location.search).entries());
      const resp = await fetch(sdkWrapperURL("/users/check-pincode"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: params.token },
        body: JSON.stringify({ product_id: selectedPackageID, pincode: pincode }),
      });
      const resp_json = await resp.json();
      if (resp_json.statusCode?.toString().startsWith("2")) {
        setPincodeAvailability("Available");
      } else throw new Error(resp_json.message);
    } catch {
      setPincodeAvailability("Unavailable");
    } finally {
      setBtnLoading(false);
    }
  };

  const bookTest = async () => {
    setBtnLoading(true);
    try {
      const params = Object.fromEntries(new URLSearchParams(document.location.search).entries());
      const resp = await fetch(sdkWrapperURL("/users/add-saved-products"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: params.token },
        body: JSON.stringify({
          product_id: selectedPackageID,
          is_saved: 1,
          application_info: {
            Price: selectedPackage.structure_val?.discounted_price ?? 0,
            Pincode: pincode,
            "Email ID": params.email,
            "Phone No.": params.phone,
            "Package Name": selectedPackage.product_name ?? "",
            "Customer Name": params.name,
          },
        }),
      });
      const resp_json = await resp.json();
      if (resp_json.statusCode?.toString().startsWith("2")) {
        window?.ReactNativeWebView?.postMessage?.("success");
      } else throw new Error(resp_json.message);
    } catch {
      window?.ReactNativeWebView?.postMessage?.("error");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="h-screen">
          <LoadingScreen />
        </div>
      ) : selectedPackageID.length > 0 ? (
        <div className="mb-4 p-3 rounded-xl bg-[#E6F2FC]">
          <div className="flex justify-between items-start space-x-4">
            <div className="shrink-0">
              <img
                className="w-20 h-20 rounded-lg bg-black"
                src={selectedPackage.structure_val?.logo}
                alt="Company Logo"
              />
            </div>
            <div className="grow">
              <h4 className="text-primary text-xs font-medium">
                {selectedPackage.structure_val?.name}
                &nbsp;(
                {selectedPackage.structure_val?.tests_included &&
                  Object.values(selectedPackage.structure_val.tests_included).reduce(
                    (sum, arr) => sum + (arr?.length ?? 0),
                    0
                  )}
                &nbsp;tests)
              </h4>
              <p className="mt-1 flex items-center">
                <span className="text-neutral-600 text-[10px] line-through">
                  {parseInt(selectedPackage.structure_val?.mrp ?? 0).toLocaleString(undefined, {
                    style: "currency",
                    currency: "INR",
                  })}
                </span>
                <span className="ml-3 text-primary text-xs font-semibold">
                  {parseInt(selectedPackage.structure_val?.discounted_price ?? 0).toLocaleString(undefined, {
                    style: "currency",
                    currency: "INR",
                  })}
                  /-
                </span>
                <span className="ml-6 text-[5px] text-black">inclusive all taxes</span>
              </p>
              <p className="inline-block mt-1.5 px-4 py-2 border border-dashed border-current bg-[#F3FBF4] text-[#1AAB2A] text-xs font-semibold">
                {Math.round(
                  (1 -
                    (selectedPackage.structure_val?.discounted_price ?? 0) /
                      (selectedPackage.structure_val?.mrp ?? 0)) *
                    100
                )}
                % Off
              </p>
            </div>
          </div>
          <div className="my-6">
            <div className="flex items-center">
              <input
                className={`px-3 py-1.5 w-48 rounded-md border ${
                  pincodeAvailability === "Unavailable" ? "border-[#f00]" : "border-[#bdbdbd]"
                } text-xs text-primary outline-none`}
                id="pincode"
                name="pincode"
                type="number"
                inputMode="numeric"
                maxLength={6}
                minLength={6}
                autoComplete="postal-code"
                placeholder="Enter Pincode"
                value={pincode}
                onChange={(e) => {
                  setPincodeAvailability("none");
                  if (e.target.value.length <= 6) setPincode(e.target.value);
                }}
                disabled={btnLoading}
              />
              {pincodeAvailability === "Available" ? (
                <>
                  <svg className="shrink-0 ml-2 h-4 w-4" viewBox="0 0 15 15" fill="none">
                    <path
                      d="M7.5 1.25C4.05 1.25 1.25 4.05 1.25 7.5C1.25 10.95 4.05 13.75 7.5 13.75C10.95 13.75 13.75 10.95 13.75 7.5C13.75 4.05 10.95 1.25 7.5 1.25ZM6.25 10.625L3.125 7.5L4.00625 6.61875L6.25 8.85625L10.9937 4.1125L11.875 5L6.25 10.625Z"
                      fill="#128A34"
                    />
                  </svg>
                  <span className="ml-1 text-[10px] text-[#373737] font-semibold">Available</span>
                </>
              ) : (
                pincodeAvailability === "Unavailable" && (
                  <>
                    <svg className="shrink-0 ml-2 h-4 w-4" viewBox="0 0 15 15" fill="none">
                      <path
                        d="M7.5 1.25C4.05625 1.25 1.25 4.05625 1.25 7.5C1.25 10.9437 4.05625 13.75 7.5 13.75C10.9437 13.75 13.75 10.9437 13.75 7.5C13.75 4.05625 10.9437 1.25 7.5 1.25ZM9.6 8.9375C9.78125 9.11875 9.78125 9.41875 9.6 9.6C9.50625 9.69375 9.3875 9.7375 9.26875 9.7375C9.15 9.7375 9.03125 9.69375 8.9375 9.6L7.5 8.1625L6.0625 9.6C5.96875 9.69375 5.85 9.7375 5.73125 9.7375C5.6125 9.7375 5.49375 9.69375 5.4 9.6C5.21875 9.41875 5.21875 9.11875 5.4 8.9375L6.8375 7.5L5.4 6.0625C5.21875 5.88125 5.21875 5.58125 5.4 5.4C5.58125 5.21875 5.88125 5.21875 6.0625 5.4L7.5 6.8375L8.9375 5.4C9.11875 5.21875 9.41875 5.21875 9.6 5.4C9.78125 5.58125 9.78125 5.88125 9.6 6.0625L8.1625 7.5L9.6 8.9375Z"
                        fill="#FA5151"
                      />
                    </svg>
                    <span className="ml-1 text-[10px] text-[#373737] font-semibold">Unavailable</span>
                  </>
                )
              )}
            </div>
            <button
              className="mt-3 px-3 py-1.5 rounded-full bg-primary text-white text-[10px]"
              type="button"
              onClick={pincodeAvailability === "Available" ? bookTest : checkPincode}
              disabled={btnLoading}
            >
              {pincodeAvailability === "Available" ? "Book Test" : "Check Availability"}&nbsp;&nbsp;&nbsp;&rarr;
            </button>
          </div>
          <div className="mt-3">
            <h5 className="text-[10px] text-primary font-semibold">How Full Body Checkup helps you?</h5>
            <p className="mt-3 text-[8px] text-[#414141]">
              {selectedPackage.structure_val?.description?.["How Full Body Checkup helps you?"]}
            </p>
          </div>
          <div className="mt-3">
            <h5 className="text-[10px] text-primary font-semibold">Precautions</h5>
            <p className="mt-3 p-2 rounded-md border border-dashed border-current text-[8px] text-[#414141] bg-[#f5f5f5]">
              {selectedPackage.structure_val?.description?.["Precautions"]}
            </p>
          </div>
          <div className="mt-6">
            <h4 className="text-[10px]">
              <span className="text-primary font-semibold">Test includes&nbsp;</span>
              <span className="text-primary/60">
                (
                {selectedPackage.structure_val?.tests_included &&
                  Object.values(selectedPackage.structure_val.tests_included).reduce(
                    (sum, arr) => sum + (arr?.length ?? 0),
                    0
                  )}
                &nbsp;tests):
              </span>
            </h4>
            <div className="mt-4 max-w-[250px]">
              {Object.entries(selectedPackage?.structure_val?.tests_included)?.map(([label, list]) => (
                <Disclosure as="div" className="mb-2.5">
                  <Disclosure.Button className="flex w-full justify-between items-start text-primary text-[10px]">
                    {({ open }) => (
                      <>
                        <span className="text-left font-semibold">
                          {label}&nbsp;
                          <span className="text-primary/[0.55]">(includes {list.length ?? 0} tests)</span>
                        </span>
                        <svg
                          className={`shrink-0 h-5 w-5 ${open ? "rotate-0" : "rotate-180"}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
                            fillRule="evenodd"
                            clipRule="evenodd"
                          />
                        </svg>
                      </>
                    )}
                  </Disclosure.Button>
                  <Disclosure.Panel as="ul" className="mt-1 list-disc list-inside text-primary text-[10px] font-medium">
                    {list.map((listItem) => (
                      <li>{listItem}</li>
                    ))}
                  </Disclosure.Panel>
                </Disclosure>
              ))}
            </div>
          </div>
        </div>
      ) : (
        packages.map((p) => (
          <div
            key={p.product_id}
            className="flex justify-between items-center space-x-4 mb-4 p-3 rounded-xl bg-[#d4d9eb]"
          >
            <div className="shrink-0">
              <img className="w-20 h-20 rounded-lg bg-black" src={p.structure_val?.logo} alt="Company Logo" />
            </div>
            <div className="grow">
              <h4 className="text-primary text-xs font-medium">
                {p.structure_val?.name}
                &nbsp;(
                {p.structure_val?.tests_included &&
                  Object.values(p.structure_val.tests_included).reduce((sum, arr) => sum + (arr?.length ?? 0), 0)}
                &nbsp;tests)
              </h4>
              <p>
                <span className="text-neutral-600 text-[10px] line-through">
                  {parseInt(p.structure_val?.mrp ?? 0).toLocaleString(undefined, {
                    style: "currency",
                    currency: "INR",
                  })}
                </span>
                <span className="ml-3 text-primary text-xs font-semibold">
                  {parseInt(p.structure_val?.discounted_price ?? 0).toLocaleString(undefined, {
                    style: "currency",
                    currency: "INR",
                  })}
                  /-
                </span>
              </p>
              <button
                className="mt-1 px-3 py-1.5 rounded-full bg-primary text-white text-[10px]"
                type="button"
                onClick={() => setSelectedPackageID(p.product_id)}
              >
                Book Now&nbsp;&nbsp;&nbsp;&rarr;
              </button>
            </div>
            <div className="shrink-0 h-14 w-14 p-1 flex flex-col justify-center items-center rounded-full border border-dashed border-secondary bg-[#E9F2FD]">
              <p className="text-primary text-[10px]">Upto</p>
              <p className="text-primary text-xs font-semibold">
                {Math.round((1 - (p.structure_val?.discounted_price ?? 0) / (p.structure_val?.mrp ?? 0)) * 100)}%
              </p>
              <p className="text-primary text-[10px]">off</p>
            </div>
          </div>
        ))
      )}
      <div className="mt-8">
        <h3 className="mb-4 text-sm text-secondary font-medium">Frequently Asked Questions</h3>
        {[
          {
            label: "How do I get the reports for my order?",
            desc: "You will receive a soft copy of the reports via email to the registered email id. The maximum timeframe is 48 hours.\n\nPlease reach out to us on 033-41816533 for any further assistance.",
          },
          {
            label: "How do I check my diagnostics order status?",
            desc: "Please reach out to us on 033-41816533 for any further assistance.",
          },
          {
            label: "Will I need a prescription for booking a test?",
            desc: "Careplix allows you to get the test done even when you don't have a prescription. You can directly select your desired test from the wide lists of options available in the App. We'll call you to confirm the test/s once the test is booked.\n\nPlease reach out to us on 033-41816533 for any further assistance.",
          },
          {
            label: "How can I modify the Tests or Appointment time in my diagnostics order?",
            desc: "If you wish to modify any of the tests or the package ordered in your Diagnostics order, please reach out to us on 033-41816533. We will try our best to accommodate your request as much as possible.",
          },
        ].map((listItem, listItemIndex) => (
          <Disclosure
            key={`faq-${listItemIndex}`}
            as="div"
            className={`mb-2.5 p-2.5 ${listItemIndex % 2 ? "bg-[#F3F3F3]" : "bg-[#F2F7FB]"}`}
          >
            <Disclosure.Button className="flex w-full justify-between items-start">
              {({ open }) => (
                <>
                  <span className="text-primary text-xs text-left">{listItem.label}</span>
                  <svg
                    className={`shrink-0 ml-3 h-5 w-5 text-secondary ${open ? "rotate-0" : "rotate-180"}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
                      fillRule="evenodd"
                      clipRule="evenodd"
                    />
                  </svg>
                </>
              )}
            </Disclosure.Button>
            <Disclosure.Panel as="p" className="mt-1 mr-8 whitespace-pre-line text-primary/75 text-[10px]">
              {listItem.desc}
            </Disclosure.Panel>
          </Disclosure>
        ))}
      </div>
    </div>
  );
};

export default LabPackages;
